import { useEffect, useRef } from 'react';

import { TrackerVideoEvent } from '@bonusly/common-action-input';
import { useAnalytics } from '@bonusly/who-am-i';

const useVideoTracking = (videoRef: HTMLVideoElement | null, videoId: string | undefined) => {
	const playStartTime = useRef(0);
	const totalPlayDuration = useRef(0);
	const { track } = useAnalytics();

	useEffect(() => {
		if (videoRef) {
			const video = videoRef;

			// Track the play action
			const handlePlayTracking = () => {
				playStartTime.current = Date.now(); // Capture when play starts
				track(TrackerVideoEvent.VideoBonusPlay, { video_id: videoId });
			};

			// We don't reset the totalPlayDuration on play to allow for tracking across multiple views
			const handleEndedTracking = () => {
				handlePauseTracking(); // Log final play duration
				track(TrackerVideoEvent.videoBonusEnd, { video_id: videoId });
			};

			const handlePauseTracking = () => {
				if (playStartTime.current) {
					const sessionPlayDuration = (Date.now() - playStartTime.current) / 1000; // Convert to seconds
					totalPlayDuration.current += sessionPlayDuration;

					track(TrackerVideoEvent.videoBonusPause, {
						video_id: videoId,
						session_duration: sessionPlayDuration,
						total_play_duration: totalPlayDuration.current,
					});
					playStartTime.current = 0; // Reset after logging
				}
			};

			const handleUnmuteTracking = () => {
				if (!video.muted) {
					track(TrackerVideoEvent.videoBonusUnmute, {
						video_id: videoId,
					});
				}
			};

			video.addEventListener('play', handlePlayTracking);
			video.addEventListener('ended', handleEndedTracking);
			video.addEventListener('pause', handlePauseTracking);
			video.addEventListener('volumechange', handleUnmuteTracking);

			return () => {
				video.removeEventListener('play', handlePlayTracking);
				video.removeEventListener('pause', handlePauseTracking);
				video.removeEventListener('ended', handleEndedTracking);
				video.removeEventListener('volumechange', handleUnmuteTracking);
			};
		}
	}, [videoRef, videoId, track]);
};

export default useVideoTracking;
